import { Image } from '@byecode/ui'
import {
    type ActorEvent,
    type ButtonItem,
    EVENT_TYPE,
    type HighLightConditions,
    type RecordLikeProtocol,
    type RichTextContentProtocol,
    type TableViewOptions,
    type ViewField
} from '@lighthouse/core'
import {
    getImageFullUrlInApplication,
    selectRecordFromPool,
    useActionRunningLoadings,
    useApplicationContext,
    useAtomData
} from '@lighthouse/shared'
import { Checkbox } from '@mantine/core'
import type { VirtualItem } from '@tanstack/react-virtual'
import cls from 'classnames'
import React, { memo, useCallback, useDeferredValue, useMemo } from 'react'
import styled, { css } from 'styled-components'

import { RecordOperator } from '../../components/RecordOperator'
import { useViewBlockContext } from '../../context'
import { getImageFromField } from '../../utils'
import { TableChecker } from './TableChecker'
import { TableValue } from './TableValue'
import { getAvatarWidth, getDesignStyleMap } from './utils'

const SCxTableCells = styled.div<{ paddingTop?: boolean; borderTop?: boolean }>`
    width: 100%;
    border-top: ${({ borderTop }) => (borderTop ? '1px solid var(--color-gray-200)' : 'none')};
`

const SCxTableSuffix = styled.div<{ sticky?: boolean; borderTop?: boolean }>`
    width: 8px;
    height: 100%;
    overflow-y: visible;
    border-top: ${({ borderTop }) => (borderTop ? '1px solid var(--color-gray-200)' : 'none')};
    ${props =>
        props.sticky &&
        css`
            position: sticky;
            top: 0;
            right: 0;
            z-index: 1;
        `}
`

const SCxTableDot = styled.div<{ borderTop?: boolean }>`
    width: 36px;
    height: 100%;
    overflow-y: visible;
    border-top: ${({ borderTop }) => (borderTop ? '1px solid var(--color-gray-200)' : 'none')};
    position: sticky;
    top: 0;
    right: 8px;
    z-index: 1;
`

const SCxTableOperator = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 36px;
    height: 100%;
`

const SCxRemain = styled.div<{ borderRadius?: boolean }>`
    width: 8px;
    height: 100%;
    ${props =>
        props.borderRadius &&
        css`
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
        `}
    overflow: hidden;
`

const SCxTableAvatarWrapper = styled.div<{ width: number; borderTop?: boolean }>`
    position: sticky;
    top: 0;
    left: 8px;
    height: 100%;
    width: ${({ width }) => `${width}px`};
    overflow: hidden;
    z-index: 1;
    /* background-color: var(--color-white); */
    flex-shrink: 0;
    font-size: var(--font-size-normal);
    border-top: ${({ borderTop }) => (borderTop ? '1px solid var(--color-gray-200)' : 'none')};
`

const SCxTableAvatarBackground = styled.div`
    width: 100%;
    height: 100%;
    padding-left: 4px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
`

const SCxTablePrimary = styled.div<{ borderTop?: boolean; left: number }>`
    position: sticky;
    top: 0;
    left: ${({ left }) => `${left + 8}px`};
    height: 36px;
    max-width: calc(100% - 60px);
    line-height: 36px;
    padding-top: 6px;
    padding-left: 0;
    overflow: hidden;
    z-index: 1;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-shrink: 0;
    font-size: var(--font-size-normal);
    border-top: ${({ borderTop }) => (borderTop ? '1px solid var(--color-gray-200)' : 'none')};
`

interface SCxTableRecordWrapperStyledParams {
    clickable?: boolean
    isMobile?: boolean
    isStripe?: boolean
    hoverColor: string
}
const SCxTableRecordWrapper = styled.div<SCxTableRecordWrapperStyledParams>`
    ${props => {
        return (
            props.isStripe &&
            css`
                background-color: var(--color-gray-100);
            `
        )
    }}

    &.record-active {
        ${props => {
            return css`
                background-color: ${props.hoverColor};
            `
        }}
    }

    ${props => {
        if (props.clickable) {
            return css`
                cursor: pointer;

                &:hover {
                    background-color: ${props.hoverColor};
                }
            `
        }
    }}
`

const SCxTableRecord = styled.div`
    position: relative;
    display: flex;
    height: 100%;
`

interface TableRecordProps {
    recordOperatorActions?: ButtonItem[]
    id: string
    rowHeight: number
    rowWidth: number
    isMobile?: boolean
    selectedRecords: string[]
    checkable?: boolean
    index: number
    scrollRef: React.RefObject<HTMLDivElement>
    blockWidth: number
    virtualItems: VirtualItem[]
    viewFields: ViewField[]
    primaryFieldId?: string
    rowStyleConditions: Record<string, HighLightConditions>
    cellStyleConditions: Record<string, HighLightConditions>
    designConfig: TableViewOptions
    hoverColor: string
    stripeHoverColor: string
    onRecordSelect?: (recordIds: string[]) => void
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record: RecordLikeProtocol) => Promise<void>
    recordClickable?: boolean
    onRenderButtonTitle: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
}

const TableRecord: React.FC<TableRecordProps> = memo(
    ({
        id,
        rowHeight,
        isMobile,
        selectedRecords = [],
        checkable,
        index,
        virtualItems,
        viewFields,
        primaryFieldId = '',
        cellStyleConditions,
        designConfig,
        hoverColor,
        stripeHoverColor,
        onRecordSelect,
        onRecordOperatorActionTrigger,
        onRecordClick,
        onRenderButtonTitle,
        recordClickable,
        recordOperatorActions
    }) => {
        const { uniqueId, blockData, dataSource, recordPoolAtom } = useViewBlockContext()
        const { id: blockId, config } = blockData
        const { eventList } = config
        const { id: dsId, schema } = dataSource
        // 使用 Map 的 O(1) 查找，useMemo 保持 selector 引用稳定
        const selector = useMemo(() => selectRecordFromPool(dsId, id), [dsId, id])
        const record = useDeferredValue(useAtomData(recordPoolAtom, selector))
        const { id: recordId, content: recordContent } = record || ({ id, dsId, content: {} } as RecordLikeProtocol)
        const { designStyle, listAvatar = '', isShowListAvatar, avatarType } = designConfig
        const isAvatar = useMemo(() => isShowListAvatar && listAvatar && schema?.[listAvatar], [isShowListAvatar, listAvatar, schema])
        const { appId } = useApplicationContext()
        const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()
        const avatarWidth = useMemo(() => {
            if (isAvatar) {
                return getAvatarWidth(isMobile)
            }
            return 0
        }, [isAvatar, isMobile])
        const recordTop = useMemo(() => (isMobile ? 36 : 0), [isMobile])
        const recordClickEvent = useMemo(() => eventList?.find(ev => ev.type === EVENT_TYPE.RECORD_CLICK), [eventList])
        const designStyleOption = useMemo(() => getDesignStyleMap(designStyle), [designStyle])
        const { isStripe, isBorderTop, isBorderRadius, isExtraBorderTop } = designStyleOption
        const isRecordBorderTop = useMemo(() => isBorderTop && index > 0, [index, isBorderTop])
        const isRecordStripe = isStripe && index % 2 === 0
        const isRecordExtraBorderTop = useMemo(() => isExtraBorderTop && index > 0, [index, isExtraBorderTop])

        const handleSelectRecord = useCallback(
            (checked: boolean, id: string) => {
                if (checked) {
                    onRecordSelect?.([...selectedRecords, id])
                } else {
                    onRecordSelect?.(selectedRecords.filter(rId => rId !== id))
                }
            },
            [onRecordSelect, selectedRecords]
        )
        const handleRecordClick = useCallback(() => {
            if (!onRecordClick || !record || !recordClickEvent) {
                return
            }
            handleActionTriggerWithLoading({
                id: uniqueId,
                actorEvent: recordClickEvent,
                trigger: (id, actorEvent) => onRecordClick(id, actorEvent, record)
            })
        }, [handleActionTriggerWithLoading, onRecordClick, record, recordClickEvent, uniqueId])

        const handleActionTrigger = useCallback(
            async (id: string, actorEvent: ActorEvent) => {
                await onRecordOperatorActionTrigger?.(id, actorEvent, record)
            },
            [onRecordOperatorActionTrigger, record]
        )

        const handleRenderButtonTitle = useCallback(
            (v: RichTextContentProtocol) => {
                return onRenderButtonTitle(v, record)
            },
            [onRenderButtonTitle, record]
        )

        const handleContent = useMemo(() => {
            if (!recordOperatorActions || recordOperatorActions.length === 0) {
                return null
            }
            return (
                <SCxTableDot borderTop={isRecordBorderTop} onClick={ev => ev.stopPropagation()}>
                    <SCxTableOperator>
                        {isMobile && selectedRecords && selectedRecords.length > 0 ? (
                            <Checkbox
                                size="xs"
                                disabled={!checkable}
                                checked={selectedRecords.includes(recordId)}
                                onChange={ev => {
                                    if (!checkable) {
                                        return
                                    }
                                    handleSelectRecord(ev.target.checked, recordId)
                                }}
                            />
                        ) : (
                            <RecordOperator
                                uniqueId={uniqueId}
                                actions={recordOperatorActions}
                                recordId={recordId}
                                record={record}
                                onActionTrigger={handleActionTrigger}
                                onRenderButtonTitle={handleRenderButtonTitle}
                            />
                        )}
                    </SCxTableOperator>
                </SCxTableDot>
            )
        }, [
            recordOperatorActions,
            isRecordBorderTop,
            isMobile,
            selectedRecords,
            checkable,
            recordId,
            uniqueId,
            record,
            handleActionTrigger,
            handleRenderButtonTitle,
            handleSelectRecord
        ])

        if (!record) {
            return null
        }

        return (
            <SCxTableRecordWrapper
                key={recordId}
                hoverColor={isRecordStripe ? stripeHoverColor : hoverColor}
                isMobile={isMobile}
                isStripe={isRecordStripe}
                className={cls({ 'record-active': selectedRecords?.includes(recordId) })}
                style={{ height: `${rowHeight}px`, cursor: loadings[recordId] ? 'wait' : 'pointer' }}
                clickable={recordClickable}
                data-key={`${blockId}&${recordId}`}
                data-record-id={recordId}
                onClick={handleRecordClick}
            >
                <SCxTableRecord>
                    <TableChecker
                        disableCheck={isMobile}
                        borderTop={isRecordBorderTop}
                        extraBorderTop={isRecordExtraBorderTop}
                        borderRadius={isBorderRadius}
                        height={rowHeight}
                        checkable={checkable}
                        isRecordSelecting={selectedRecords.length > 0}
                        index={index}
                        checked={selectedRecords.includes(recordId)}
                        onChange={checked => handleSelectRecord(checked, recordId)}
                    />
                    {isAvatar && (
                        <SCxTableAvatarWrapper width={avatarWidth} borderTop={isRecordBorderTop}>
                            <SCxTableAvatarBackground>
                                <Image
                                    src={getImageFullUrlInApplication(appId, getImageFromField(recordContent?.[listAvatar]))}
                                    radius={avatarType === 'round' ? 16 : 4}
                                    width={32}
                                    height={32}
                                    alt="Norway"
                                    placeholderNode={<div style={{ background: 'var(--color-gray-200)' }} />}
                                />
                            </SCxTableAvatarBackground>
                        </SCxTableAvatarWrapper>
                    )}
                    {isMobile && (
                        <SCxTablePrimary borderTop={isRecordBorderTop} left={avatarWidth}>
                            {schema?.[primaryFieldId] && (
                                <TableValue
                                    isBold
                                    dsId={dsId}
                                    recordId={recordId}
                                    field={schema[primaryFieldId]}
                                    data={recordContent?.[primaryFieldId]?.value}
                                />
                            )}
                        </SCxTablePrimary>
                    )}
                    <SCxTableCells borderTop={isRecordBorderTop}>
                        {virtualItems.map(virtualColumn => {
                            const curField = viewFields[virtualColumn.index]
                            if (!curField) {
                                return null
                            }
                            const cId = curField.fieldId
                            const field = schema?.[cId]
                            const cData = recordContent?.[cId]

                            if (!field) {
                                return null
                            }
                            const styleConditions = cellStyleConditions?.[cId] || []
                            return (
                                <TableValue
                                    key={cId}
                                    isBold={!isMobile && virtualColumn.index === 0}
                                    dsId={dsId}
                                    recordId={recordId}
                                    style={{
                                        position: 'absolute',
                                        top: recordTop,
                                        bottom: 0,
                                        left: 0,
                                        width: `${virtualColumn.size}px`,
                                        transform: `translateX(${virtualColumn.start}px)`
                                    }}
                                    width={curField.width}
                                    field={field}
                                    data={cData?.value}
                                    styleConditions={styleConditions}
                                />
                            )
                        })}
                    </SCxTableCells>
                    {handleContent}
                    <SCxTableSuffix sticky borderTop={isExtraBorderTop && index > 0}>
                        <SCxRemain borderRadius={isBorderRadius} />
                    </SCxTableSuffix>
                </SCxTableRecord>
            </SCxTableRecordWrapper>
        )
    }
)

export default TableRecord
